import { useLocation, useNavigate } from 'react-router-dom'
import { useLanguageStore, useTranslation } from '@/services/i18n'
import { localizedPath, stripLocalePrefix } from '@/services/i18n/localeRoutes'
import { useGlobeStore } from './globeStore'

/**
 * Mounted once globally (see App.tsx), not per-page — a "back to the globe"
 * affordance that any current or future non-home route gets automatically,
 * rather than each page needing to remember to add its own. Hidden on '/'
 * itself since you're already looking at the globe there, and hidden on the
 * /models gallery routes since those pages render their own explicit,
 * clearly-labeled "Back to Globe" button in-flow (a kids' app needs more
 * than a small unlabeled icon) — keeping this one mounted there too would
 * just reserve dead top-of-screen space for a redundant control.
 */
export function BackToGlobeButton() {
  const location = useLocation()
  const navigate = useNavigate()
  const clearTarget = useGlobeStore((s) => s.clearTarget)
  const locale = useLanguageStore((s) => s.locale)
  const { t } = useTranslation()

  const agnosticPath = stripLocalePrefix(location.pathname)
  if (
    agnosticPath === '/' ||
    agnosticPath.startsWith('/models') ||
    agnosticPath.startsWith('/print/') ||
    agnosticPath === '/teachers' ||
    agnosticPath.startsWith('/teachers/') ||
    agnosticPath.startsWith('/classroom') ||
    agnosticPath.startsWith('/present/') ||
    agnosticPath.startsWith('/learn/')
  ) return null

  function handleClick() {
    // Leaving a page entirely (not jumping to another place) — clear the
    // leftover 'arrived' target so the globe shows the free-roam view
    // instead of instantly popping the same discovery card back open.
    clearTarget()
    navigate(localizedPath(locale, '/'))
  }

  return (
    <button
      onClick={handleClick}
      aria-label={t('home.backToGlobeAria')}
      className="pointer-events-auto fixed z-40 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-black/40 backdrop-blur-md transition hover:bg-black/60 top-[max(1rem,env(safe-area-inset-top))] left-[max(1rem,env(safe-area-inset-left))]"
    >
      {/* 64x64 favicon derivative, not the 1254x1254 master — see HomePage.tsx's
          identical swap for why. */}
      <img src="/kaleido-favicon-64.png" alt="" className="h-6 w-6 rounded-lg" />
    </button>
  )
}
